import { FC, MouseEvent, useState } from 'react'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../atoms/dialog'
import Typography from '../../atoms/Typography'
import FilterDropdown from '../../atoms/FilterDropDown'
import { PrimaryButton } from '../../atoms/buttons/PrimaryButton'
import { SecondaryButton } from '../../atoms/buttons/SecondaryButton'
import FilterTrigger from '../../molecules/FilterTrigger'
import TenantAccessSelector from '../../molecules/TenantAccessSelector'
import { api } from 'src/api/root'
import { EMPTY_LAYER_FILTERS, useLayerFilters } from 'src/enviroment/hooks/useLayerFilters'
import { capitalizeText } from 'src/enviroment/lib/utils'
import { LAYER_FILTER_NONE, LayerFilters, LayerUploadStatus } from 'src/types/layer'

// The enum is shared with project uploads; UPLOADING and PENDING remain only on the deprecated chunk path.
const LIBRARY_STATUSES = [LayerUploadStatus.COMPLETED, LayerUploadStatus.NEEDS_REVIEW, LayerUploadStatus.ERROR]

const STATUS_OPTIONS = LIBRARY_STATUSES.map((status) => ({
  id: status,
  name: capitalizeText(status.replace(/_/g, ' ')),
}))

interface LayerFilterDialogProps {
  filters: ReturnType<typeof useLayerFilters>
}

const LayerFilterDialog: FC<LayerFilterDialogProps> = ({ filters: { filters, setFilters, activeFilterCount } }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [draft, setDraft] = useState<LayerFilters>(EMPTY_LAYER_FILTERS)
  const { data: countries = [] } = api.layer.countries.useQuery({ enabled: isOpen })
  const { data: datasets = [] } = api.dataset.list.useQuery({}, { enabled: isOpen })

  const updateDraft = (change: Partial<LayerFilters>) => setDraft((current) => ({ ...current, ...change }))

  const handleOpenChange = (open: boolean) => {
    if (open) setDraft(filters)
    setIsOpen(open)
  }

  const handleClear = (e: MouseEvent) => {
    e.stopPropagation()
    setFilters(EMPTY_LAYER_FILTERS)
  }

  const handleSave = () => {
    setFilters(draft)
    setIsOpen(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <FilterTrigger
          className="w-auto shrink-0"
          activeLabel={activeFilterCount > 0 ? `${activeFilterCount} active` : null}
          onClear={handleClear}
        />
      </DialogTrigger>

      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[800px]" aria-description="layer-filter-dialog">
        <DialogHeader>
          <DialogTitle>
            <Typography variant="xl">Filter</Typography>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2">
          <FilterDropdown
            type="Country"
            options={[
              { id: LAYER_FILTER_NONE, name: 'Global' },
              ...countries.map((country) => ({ id: country, name: country })),
            ]}
            selectedIds={draft.countries}
            onChange={(ids) => updateDraft({ countries: ids })}
          />
          <FilterDropdown
            type="Dataset"
            options={[{ id: LAYER_FILTER_NONE, name: 'None' }, ...datasets]}
            selectedIds={draft.datasetIds}
            onChange={(ids) => updateDraft({ datasetIds: ids })}
          />
          <FilterDropdown
            type="Status"
            options={STATUS_OPTIONS}
            selectedIds={draft.statuses}
            onChange={(ids) => updateDraft({ statuses: ids })}
          />
        </div>

        <Typography variant="sm" className="text-gray-400">
          Tenants: shows layers added to at least one project of the selected tenants. Access granted to a tenant
          doesn&apos;t count, and neither do archived projects or layers with no data inside the project&apos;s area.
        </Typography>

        <TenantAccessSelector
          selectedTenantIds={draft.tenantIds}
          onChange={(ids) => updateDraft({ tenantIds: ids })}
          enabled={isOpen}
          hideAccessStatus
        />

        <DialogFooter className="mt-6 flex justify-between sm:justify-between">
          <SecondaryButton onClick={() => setIsOpen(false)}>Back</SecondaryButton>
          <PrimaryButton onClick={handleSave}>Save</PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default LayerFilterDialog
